import {
  DEFAULT_PROJECT_SORT,
  PROJECT_SORTS,
  type ProjectSort,
} from '../project-sort'

import type { FindPublicProjectsDto } from '@brickup/shared/dto'
import type { PropertyType } from '@brickup/shared/enums'

/**
 * What a visitor has narrowed the published objects to.
 *
 * The server's own query shape, cut to what the bar offers: the list endpoint
 * does the narrowing, so the bar writes exactly the parameters it accepts and
 * there is no second filter implementation to drift from it. The endpoint
 * still takes the rest — an address that carries one simply goes unread.
 */
export type ProjectFilters = Pick<
  FindPublicProjectsDto,
  | 'propertyType'
  | 'marketPriceMin'
  | 'marketPriceMax'
  | 'livingAreaMin'
  | 'livingAreaMax'
>

/** How many filters are set, which is what the bar and the count report. */
export function activeFilterCount(filters: ProjectFilters): number {
  return Object.values(filters).filter(value => value !== undefined).length
}

/** Drops the keys a group owns, leaving the rest of the narrowing in place. */
export function withoutKeys(
  filters: ProjectFilters,
  keys: (keyof ProjectFilters)[],
): ProjectFilters {
  const next = { ...filters }
  for (const key of keys) delete next[key]
  return next
}

/**
 * The whole screen's state in the address bar, so a narrowed list can be
 * shared, reloaded and stepped back out of.
 *
 * The keys are the server's own, spelled as the query spells them: one name
 * per bound, rather than a second vocabulary to keep in step with it.
 */
export type ProjectsSearch = ProjectFilters & { sort?: ProjectSort }

/**
 * Every bound the address carries, as a `Record` rather than a list: a bound
 * added to `ProjectFilters` without a line here fails the build, where a list
 * would compile and leave the new filter silently unreadable from a shared
 * link. `propertyType` is not a number and is read separately below.
 */
const NUMBER_KEYS: Record<
  Exclude<keyof ProjectFilters, 'propertyType'>,
  true
> = {
  marketPriceMin: true,
  marketPriceMax: true,
  livingAreaMin: true,
  livingAreaMax: true,
}

const PROPERTY_TYPES = ['APARTMENT', 'HOUSE'] as const

/**
 * Reads the address bar into state, dropping anything it does not recognise.
 *
 * A hand-typed or stale address is a visitor's first impression of the page,
 * so an unusable value means that one filter is absent — never a crash and
 * never a blank list.
 */
export function parseProjectsSearch(
  search: Record<string, unknown>,
): ProjectsSearch {
  const parsed: ProjectsSearch = {}

  for (const key of Object.keys(NUMBER_KEYS) as (keyof typeof NUMBER_KEYS)[]) {
    if (search[key] === undefined || search[key] === '') continue
    const value = Number(search[key])
    if (Number.isFinite(value) && value >= 0) parsed[key] = value
  }

  const type = String(search.propertyType ?? '')
  if ((PROPERTY_TYPES as readonly string[]).includes(type)) {
    parsed.propertyType = type as PropertyType
  }

  const sort = String(search.sort ?? '')
  if (PROJECT_SORTS.some(entry => entry.id === sort)) {
    parsed.sort = sort as ProjectSort
  }

  return parsed
}

/** Splits the address back into the two things the screen holds. */
export function readProjectsSearch(search: ProjectsSearch): {
  filters: ProjectFilters
  sort: ProjectSort
} {
  const { sort, ...filters } = search
  return { filters, sort: sort ?? DEFAULT_PROJECT_SORT }
}

/** The address a state writes, with the default sort left out of it. */
export function writeProjectsSearch(
  filters: ProjectFilters,
  sort: ProjectSort,
): ProjectsSearch {
  return sort === DEFAULT_PROJECT_SORT ? filters : { ...filters, sort }
}
